
import { useState, useCallback, useEffect } from "react";
import { SetData } from "../../../../types";

interface UseEditSetModalLogicProps {
  isOpen: boolean;
  setData: SetData;
  setIndex: number;
  onSetDataChange: (setIndex: number, field: 'reps' | 'weight', value: string) => void;
  onOpenChange: (open: boolean) => void;
}

export const useEditSetModalLogic = ({
  isOpen,
  setData,
  setIndex,
  onSetDataChange,
  onOpenChange
}: UseEditSetModalLogicProps) => {
  const [localReps, setLocalReps] = useState(setData.reps.toString());
  const [localWeight, setLocalWeight] = useState(setData.weight.toString());
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [initialData, setInitialData] = useState(setData);

  // Initialize when modal opens
  useEffect(() => {
    if (isOpen) {
      console.log(`📖 Modal opening for set ${setIndex}:`, setData);
      setLocalReps(setData.reps.toString());
      setLocalWeight(setData.weight.toString());
      setInitialData(setData);
      setHasUnsavedChanges(false);
    }
  }, [isOpen, setIndex]); // Only depend on isOpen and setIndex

  // Track changes
  useEffect(() => {
    const repsChanged = localReps !== initialData.reps.toString();
    const weightChanged = localWeight !== initialData.weight.toString();
    setHasUnsavedChanges(repsChanged || weightChanged);
  }, [localReps, localWeight, initialData]);

  // Simple local change handlers - NO immediate syncing
  const handleRepsChange = useCallback((value: string) => {
    console.log(`📝 Local reps change for set ${setIndex}:`, value);
    setLocalReps(value);
  }, [setIndex]);

  const handleWeightChange = useCallback((value: string) => {
    console.log(`📝 Local weight change for set ${setIndex}:`, value);
    setLocalWeight(value);
  }, [setIndex]);

  // Save changes and close modal
  const handleSave = useCallback(() => {
    console.log(`💾 Saving changes for set ${setIndex}:`, { reps: localReps, weight: localWeight });
    
    // Apply changes to parent
    if (localReps !== initialData.reps.toString()) {
      onSetDataChange(setIndex, 'reps', localReps);
    }
    if (localWeight !== initialData.weight.toString()) {
      onSetDataChange(setIndex, 'weight', localWeight);
    }
    
    // Close modal
    onOpenChange(false);
  }, [setIndex, localReps, localWeight, initialData, onSetDataChange, onOpenChange]);

  // Cancel changes and close modal
  const handleCancel = useCallback(() => {
    console.log(`❌ Cancelling changes for set ${setIndex}`);
    
    // Reset to initial values
    setLocalReps(initialData.reps.toString());
    setLocalWeight(initialData.weight.toString());
    setHasUnsavedChanges(false);
    
    // Close modal
    onOpenChange(false);
  }, [initialData, onOpenChange, setIndex]);

  // Block automatic closing - only allow manual close via Save/Cancel
  const handleOpenChange = useCallback((open: boolean) => {
    if (!open) {
      // Prevent automatic closing - user must use Save or Cancel
      console.log(`🚫 Blocking automatic modal close for set ${setIndex}`);
      return;
    }
    onOpenChange(open);
  }, [onOpenChange, setIndex]);

  return {
    localReps,
    localWeight,
    isSyncing: false, // No immediate syncing
    lastSyncTime: null, // No sync timing
    handleRepsChange,
    handleWeightChange,
    handleSave,
    handleCancel,
    handleOpenChange
  };
};
